import { useState } from "react";
import { Plus, Edit, Archive, ArchiveRestore, Shield } from "lucide-react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Division, useDivisions } from "@/hooks/useDivisions";
import { DivisionForm } from "@/components/divisions/DivisionForm";
import { useUserRole } from "@/hooks/useUserRole";

export default function Divisions() {
  const [showForm, setShowForm] = useState(false);
  const [editingDivision, setEditingDivision] = useState<Division | undefined>();
  const { divisions, isLoading, deleteDivision, updateDivision } = useDivisions(true);
  const { isTenantAdmin, isLoading: roleLoading } = useUserRole();

  const activeDivisions = divisions.filter(d => d.is_active);
  const inactiveDivisions = divisions.filter(d => !d.is_active);

  const handleEdit = (division: Division) => {
    setEditingDivision(division);
    setShowForm(true);
  };

  const handleArchive = async (division: Division) => {
    await deleteDivision.mutateAsync(division.id);
  };

  const handleRestore = async (division: Division) => {
    await updateDivision.mutateAsync({
      id: division.id,
      data: {
        name: division.name,
        description: division.description || undefined,
        is_active: true,
      },
    });
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingDivision(undefined);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Divisions</h1>
            <p className="text-muted-foreground">
              Manage your business divisions and categories
            </p>
          </div>
          {isTenantAdmin && (
            <Button onClick={() => setShowForm(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Division
            </Button>
          )}
        </div>

        {!isTenantAdmin && (
          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              Only administrators can create, edit, or delete divisions. You can view existing divisions below.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Divisions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{divisions.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeDivisions.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Inactive</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{inactiveDivisions.length}</div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All Divisions</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : divisions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">
                  No divisions yet. Create your first division!
                </p>
                <Button onClick={() => setShowForm(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Division
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {divisions.map((division) => (
                    <TableRow key={division.id}>
                      <TableCell className="font-medium">{division.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {division.description || "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={division.is_active ? "default" : "secondary"}>
                          {division.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {isTenantAdmin ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(division)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {division.is_active ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleArchive(division)}
                              >
                                <Archive className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRestore(division)}
                              >
                                <ArchiveRestore className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <DivisionForm
        open={showForm && isTenantAdmin}
        onOpenChange={handleCloseForm}
        division={editingDivision}
      />
    </DashboardLayout>
  );
}
